import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAccount } from '../context/AccountContext';
import { ADMIN_PATH } from '../config/adminRoutes';
import styles from '../styles/NewsletterPopup.module.css';

const STORAGE_KEY = 'esganga-newsletter-popup';
const SHOW_AFTER_MS = 15000;
const SNOOZE_DAYS = 14;
const EXCLUDED_PREFIXES = [ADMIN_PATH, '/cart', '/checkout', '/mis-alertas'];

function readState() {
    try {
        return JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '{}');
    } catch (error) {
        return {};
    }
}

function writeState(next) {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
}

function isSnoozed(state) {
    if (state.subscribed) {
        return true;
    }
    if (!state.dismissedAt) {
        return false;
    }
    const elapsedDays = (Date.now() - state.dismissedAt) / (1000 * 60 * 60 * 24);
    return elapsedDays < SNOOZE_DAYS;
}

const NewsletterPopup = () => {
    const { user, loading: accountLoading } = useAccount();
    const location = useLocation();
    const [visible, setVisible] = useState(false);
    const [email, setEmail] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const isExcludedRoute = EXCLUDED_PREFIXES.some((prefix) => location.pathname.startsWith(prefix));

    useEffect(() => {
        if (accountLoading || user || isExcludedRoute || isSnoozed(readState())) {
            return undefined;
        }

        const timer = setTimeout(() => setVisible(true), SHOW_AFTER_MS);
        return () => clearTimeout(timer);
    }, [accountLoading, user, isExcludedRoute]);

    const close = () => {
        writeState({ ...readState(), dismissedAt: Date.now() });
        setVisible(false);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (isSubmitting) {
            return;
        }

        setIsSubmitting(true);
        try {
            const response = await fetch('/api/newsletter/subscribe', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email })
            });
            const data = await response.json();

            if (!response.ok || !data.ok) {
                throw new Error(data.error || 'No se pudo suscribir');
            }

            writeState({ ...readState(), subscribed: true });
            toast.success('Listo, cada viernes te llegan las 10 mejores gangas.');
            setVisible(false);
        } catch (error) {
            toast.error('Revisa el correo e intentalo de nuevo.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!visible) {
        return null;
    }

    return createPortal(
        <div className={styles.backdrop} onClick={close}>
            <div className={styles.modal} onClick={(event) => event.stopPropagation()} role="dialog" aria-label="Suscripcion al resumen semanal">
                <button type="button" className={styles.closeButton} onClick={close} aria-label="Cerrar">
                    &times;
                </button>

                <div className={styles.head}>
                    <span>Cada viernes, sin spam</span>
                    <strong>Las 10 mejores gangas de la semana en tu correo</strong>
                    <p>Solo ofertas que siguen activas. Un unico correo semanal, baja cuando quieras.</p>
                </div>

                <form className={styles.form} onSubmit={handleSubmit}>
                    <input
                        type="email"
                        required
                        placeholder="tucorreo@ejemplo.com"
                        value={email}
                        onChange={(event) => setEmail(event.target.value)}
                    />
                    <button type="submit" className={styles.submitButton} disabled={isSubmitting}>
                        {isSubmitting ? 'Enviando...' : 'Suscribirme'}
                    </button>
                </form>
                <button type="button" className={styles.skipLink} onClick={close}>
                    Ahora no
                </button>
            </div>
        </div>,
        document.body
    );
};

export default NewsletterPopup;
